import { and, desc, eq } from 'drizzle-orm'
import { inngest } from '@/lib/inngest'
import { db as defaultDb } from '@/db'
import { aiArtifacts, auditLog, tenantConfigs, ticketMessages, tickets } from '@/db/schema'
import { transition } from '@/lib/ticket-state-machine'
import { searchKnowledgeEntries, type KnowledgeSearchResult } from '@/lib/knowledge-search'
import { buildResponseDraftPrompt, RESPONSE_DRAFT_PROMPT_VERSION } from '../../prompts/response-draft'
import { callResponseDraftLLM, type AIGatewayConfig, type ResponseDraftResult } from '@/lib/ai-gateway'
import type { PostgresJsDatabase } from 'drizzle-orm/postgres-js'
import type * as schema from '@/db/schema'

type DB = PostgresJsDatabase<typeof schema>

export type DraftResponseInput = {
  ticketId: string
  tenantId: string
  expertId: string
  ticketVersion: number
}

export async function runResponseDraft(
  input: DraftResponseInput,
  options?: {
    db?: DB
    callLLM?: (config: AIGatewayConfig, system: string, user: string) => Promise<ResponseDraftResult>
    searchKnowledge?: typeof searchKnowledgeEntries
  },
): Promise<{ outcome: 'RESPONSE_READY' | 'STALE'; draft?: string }> {
  const db = (options?.db ?? defaultDb) as DB
  const llm = options?.callLLM ?? callResponseDraftLLM
  const searchKnowledge = options?.searchKnowledge ?? searchKnowledgeEntries

  const [ticket] = await db
    .select()
    .from(tickets)
    .where(and(eq(tickets.id, input.ticketId), eq(tickets.tenantId, input.tenantId)))
    .limit(1)

  if (!ticket) throw new Error(`Ticket not found: ${input.ticketId}`)
  if (ticket.version !== input.ticketVersion || ticket.status !== 'RESPONSE_DRAFTING') {
    return { outcome: 'STALE' }
  }

  const [config] = await db
    .select()
    .from(tenantConfigs)
    .where(eq(tenantConfigs.tenantId, input.tenantId))
    .limit(1)

  if (!config) throw new Error(`Tenant config not found for tenant: ${input.tenantId}`)

  const messages = await db
    .select({ role: ticketMessages.role, body: ticketMessages.bodySanitized })
    .from(ticketMessages)
    .where(eq(ticketMessages.ticketId, input.ticketId))
    .orderBy(ticketMessages.createdAt)

  const [summaryArtifact] = await db
    .select()
    .from(aiArtifacts)
    .where(and(eq(aiArtifacts.ticketId, input.ticketId), eq(aiArtifacts.artifactType, 'summary')))
    .orderBy(desc(aiArtifacts.createdAt))
    .limit(1)

  const query = [
    ticket.title,
    summaryArtifact?.content,
    ...messages.map((message) => message.body),
  ].filter(Boolean).join('\n')

  const sources = await searchKnowledge({ tenantId: input.tenantId, query, limit: 5 }, { db })
  const { system, user } = buildResponseDraftPrompt({
    referenceNumber: ticket.referenceNumber,
    messages,
    summary: summaryArtifact?.content ?? null,
    knowledgeEntries: sources,
    successCriteria: ticket.successCriteria ?? undefined,
    actionRequested: ticket.actionRequested ?? undefined,
  })

  let result: ResponseDraftResult
  try {
    result = await llm(
      {
        provider: config.aiProvider,
        model: config.aiModel,
        temperature: config.aiTemperature,
        dataProcessingConsent: config.dataProcessingConsent,
      },
      system,
      user,
    )
  } catch (err) {
    result = buildFallbackDraft(ticket.referenceNumber, messages, sources, err)
  }

  const now = new Date()
  const nextStatus = transition(ticket.status, 'RESPONSE_READY')

  await db.insert(aiArtifacts).values({
    tenantId: input.tenantId,
    ticketId: input.ticketId,
    triggerEvent: 'ticket/response_draft.requested',
    artifactType: 'suggested_response',
    content: result.draft,
    contentJson: JSON.stringify({
      ...result,
      sources: sources.map((source, index) => ({
        index: index + 1,
        id: source.id,
        slug: source.slug,
        title: source.title,
        score: source.score,
      })),
    }),
    modelUsed: config.aiModel,
    promptVersion: RESPONSE_DRAFT_PROMPT_VERSION,
    validationStatus: 'valid',
  })

  const [updatedTicket] = await db
    .update(tickets)
    .set({
      status: nextStatus,
      aiProcessingState: 'idle',
      version: ticket.version + 1,
      updatedAt: now,
    })
    .where(and(eq(tickets.id, input.ticketId), eq(tickets.version, ticket.version)))
    .returning()

  if (!updatedTicket) return { outcome: 'STALE' }

  await db.insert(auditLog).values({
    tenantId: input.tenantId,
    actorId: input.expertId,
    actorType: 'ai_agent',
    action: 'ticket.response_drafted',
    entityType: 'ticket',
    entityId: input.ticketId,
    payload: {
      from: ticket.status,
      to: nextStatus,
      hook: 'response-draft',
      source_count: sources.length,
      reasoning_trace: result.reasoning_trace,
    },
  })

  return { outcome: 'RESPONSE_READY', draft: result.draft }
}

function buildFallbackDraft(
  referenceNumber: string,
  messages: Array<{ role: string; body: string }>,
  sources: KnowledgeSearchResult[],
  err: unknown,
): ResponseDraftResult {
  const latestLineManagerMessage = [...messages].reverse().find((message) => message.role === 'line_manager')
  const primarySource = sources[0]
  const sourceLine = primarySource
    ? `Relevant KB: ${primarySource.title}. ${primarySource.bodyMarkdown.slice(0, 240)}`
    : 'No relevant KB entry was available.'
  const errorMessage = err instanceof Error ? err.message : 'Unknown response drafting error'

  return {
    draft: [
      `Thanks for the details on ${referenceNumber}.`,
      latestLineManagerMessage ? `I understand the issue as: ${latestLineManagerMessage.body}` : '',
      sourceLine,
      'Please confirm the current status after completing the recommended checks, and escalate to the expert team if the issue persists.',
    ].filter(Boolean).join('\n\n'),
    source_indexes: primarySource ? [1] : [],
    knowledge_gaps: sources.length === 0 ? ['No matching knowledge base entry was found for this ticket.'] : [],
    reasoning_trace: `Fallback response draft used because Hook 5 failed: ${errorMessage}`,
  }
}

export const responseDraftFn = inngest.createFunction(
  {
    id: 'response-draft',
    retries: 3,
    triggers: [{ event: 'ticket/response_draft.requested' }],
  },
  async ({ event, step }) => {
    return await step.run('run-response-draft', () =>
      runResponseDraft(event.data as DraftResponseInput),
    )
  },
)
